import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteChrome from './SiteChrome';
import api from '../services/api';

const privacyUrl = 'https://bettermedaily.co.za/privacy.html';
const mainTermsUrl = 'https://bettermedaily.co.za/terms.html';
const supportEmail = 'affiliates@bettermedaily.co.za';

const AffiliateLanding = () => {
  const [minimumPayoutAmount, setMinimumPayoutAmount] = useState(100);

  useEffect(() => {
    const loadPricing = async () => {
      try {
        const pricing = await api.getPricing();
        setMinimumPayoutAmount(Number(pricing?.affiliateMinimumPayoutAmount || 100));
      } catch (error) {
        console.warn('Failed to load affiliate pricing settings:', error);
      }
    };

    loadPricing();
  }, []);

  const affiliatePosts = [
    {
      slug: 'how-to-sell-an-app-to-your-audience-without-sounding-pushy',
      title: 'How to Sell an App to Your Audience Without Sounding Pushy',
      meta: 'Affiliates / 5 min',
    },
    {
      slug: 'how-to-promote-products-to-followers-without-losing-trust',
      title: 'How to Promote Products to Followers Without Losing Trust',
      meta: 'Affiliates / 6 min',
    },
    {
      slug: 'content-angles-that-help-creators-convert-better',
      title: 'Content Angles That Help Creators Convert Better',
      meta: 'Affiliates / 4 min',
    },
  ];

  return (
    <SiteChrome>
      {/* Hero Section */}
      <section className="pt-16 pb-16 sm:pt-24 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="mb-8">
            <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
              Turn your influence into recurring impact
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight mb-3">
              Earn while helping others grow
            </h1>
            <p className="text-base text-gray-600 mb-8 max-w-3xl mx-auto">
              Share Better Me with your audience, help them build stronger journaling, habit, and spending-awareness routines, and earn on qualified referrals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup" className="bg-primary-600 hover:bg-primary-700 text-white inline-flex items-center justify-center gap-2 text-sm px-8 py-4 rounded-xl font-semibold transition-colors shadow-lg">
                👉 Start Earning
              </Link>
              <a href="#how-it-works" className="bg-white text-primary-600 border-2 border-primary-600 hover:bg-primary-50 inline-flex items-center justify-center gap-2 text-sm px-8 py-4 rounded-xl font-semibold transition-colors">
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>


      {/* How It Works */}
      <section id="how-it-works" className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-base text-gray-600 mb-12 max-w-2xl mx-auto">
              Getting started is simple. Sign up, share your unique link, and start earning commissions today!
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-5 items-stretch">
            <div className="relative rounded-3xl bg-white border border-gray-100 p-6 shadow-sm h-full flex flex-col">
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 bg-primary-50 rounded-2xl flex items-center justify-center text-primary-700 font-bold text-lg">01</div>
                <span className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold">Start</span>
              </div>
              <div className="space-y-3">
                <h3 className="font-semibold text-base text-gray-900">Sign Up</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Join our affiliate program in less than 2 minutes.</p>
              </div>
            </div>
            <div className="relative rounded-3xl bg-slate-950 text-white p-6 shadow-lg shadow-slate-200 h-full flex flex-col">
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center text-white font-bold text-lg">02</div>
                <span className="text-[10px] uppercase tracking-[0.3em] text-primary-300 font-semibold">Share</span>
              </div>
              <div className="space-y-3">
                <h3 className="font-semibold text-base">Share & Refer</h3>
                <p className="text-slate-300 text-sm leading-relaxed">Share your unique link with friends, followers, and communities that trust your recommendations.</p>
              </div>
            </div>
            <div className="relative rounded-3xl bg-white border border-gray-100 p-6 shadow-sm h-full flex flex-col">
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 bg-primary-50 rounded-2xl flex items-center justify-center text-primary-700 font-bold text-lg">03</div>
                <span className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold">Earn</span>
              </div>
              <div className="space-y-3">
                <h3 className="font-semibold text-base text-gray-900">Earn Commissions</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Get paid for validated active referrals and confirmed Premium payments while helping people build better routines.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Earnings Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">How Much You Can Earn</h2>
            <p className="text-base text-gray-600 mb-8 max-w-2xl mx-auto">
              Our commission structure is simple, but earnings are not guaranteed and depend on valid referrals and verified payments.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                  <span className="text-green-600 font-bold">R1</span>
                </div>
                <div>
                  <h3 className="font-semibold text-base">Per Active Sign-up</h3>
                  <p className="text-gray-600 text-sm">Get R1 for every friend who signs up and uses Better Me</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                  <span className="text-blue-600 font-bold">50%</span>
                </div>
                <div>
                  <h3 className="font-semibold text-base">Premium Commission</h3>
                  <p className="text-gray-600 text-sm">Earn 50% on verified Premium payments from referred users. Refunds or suspicious activity can delay or reverse commission.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="space-y-6">
            <div className="rounded-3xl border border-gray-100 bg-white p-6 sm:p-8 shadow-sm">
              <p className="text-[10px] uppercase tracking-[0.3em] text-primary-700 font-semibold mb-3">About Better Me</p>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">A reflection app people can actually keep using</h2>
              <div className="space-y-3 text-sm text-gray-700 leading-6">
                <p>
                  Better Me helps people journal, review habits, track spending patterns, reflect on decisions, and plan with more clarity.
                </p>
                <p>
                  The AI features are designed to support reflection by helping users respond to what they write, notice themes, and think more clearly about routines and priorities.
                </p>
                <p>
                  That gives affiliates a strong, compliant message: Better Me is a practical self-reflection and planning app, not a therapy, diagnosis, crisis-support, or regulated financial-advice service.
                </p>
              </div>
              <div className="mt-6">
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center rounded-xl bg-slate-950 text-white text-sm leading-none font-semibold px-5 py-3 hover:bg-slate-800 transition-colors"
                >
                  Learn More About Better Me
                </Link>
              </div>
            </div>

            <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 sm:p-8">
              <h3 className="text-base sm:text-lg font-bold text-amber-950 mb-3">Compliant language that still converts</h3>
              <div className="space-y-3 text-sm text-amber-900 leading-6">
                <p>Lead with outcomes like reflection, pattern awareness, better routines, clearer planning, and more mindful spending.</p>
                <p>Avoid clinical, crisis, or regulated-advice language. The safest high-converting angle is practical self-awareness and daily structure.</p>
                <p>Simple phrases often work best: “a diary that talks back”, “a place to spot your patterns”, or “a better way to reflect and plan”.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="affiliate-posts" className="py-10 bg-slate-950 text-white px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-5">
            <p className="text-[10px] uppercase tracking-[0.3em] text-primary-300 font-semibold mb-2">Affiliate Blog</p>
            <h2 className="text-sm sm:text-lg font-bold mb-2">Practical guides for creators and community builders who want to earn while helping followers grow.</h2>
            <p className="text-slate-300 text-[11px] sm:text-xs leading-relaxed">Promote Better Me responsibly as a journaling, habit, and spending-awareness app.</p>
          </div>
          <div className="divide-y divide-white/10 border-t border-white/10">
            {affiliatePosts.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 py-3 text-xs sm:text-sm text-slate-100 hover:text-white transition-colors"
              >
                <span className="font-medium">{post.title}</span>
                <span className="text-[11px] uppercase tracking-[0.2em] text-slate-400">{post.meta}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Program compliance and support details</h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              These are the public rules affiliates should follow when promoting Better Me and requesting payouts.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-6">
              <h3 className="font-semibold text-gray-900 mb-2">AI output and product limits</h3>
              <p className="text-sm text-gray-600">
                Promote Better Me as journaling, habit, decision-reflection, and spending-awareness software only. Do not claim it provides therapy, diagnosis, treatment, or regulated financial advice.
              </p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-6">
              <h3 className="font-semibold text-gray-900 mb-2">No spam or fake claims</h3>
              <p className="text-sm text-gray-600">
                Use consent-based promotion only. Fake signups, bot traffic, self-referrals, income guarantees, and misleading product claims can lead to rejected referrals or account termination.
              </p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Payout details</h3>
              <p className="text-sm text-gray-600">
                Minimum payout is R{minimumPayoutAmount.toFixed(2)}. Payout requests are processed to South African bank accounts after admin review and may be paused while referral activity or bank details are verified.
              </p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Crisis handling</h3>
              <p className="text-sm text-gray-600">
                If someone mentions self-harm or severe distress, do not try to counsel them as an affiliate. Direct them to local emergency support, 112/10111 in South Africa, or Lifeline South Africa on 0861 322 322.
              </p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Terms, privacy, and support</h3>
              <p className="text-sm text-gray-600">
                Read the <Link to="/terms" className="text-primary-600 hover:underline">Affiliate Terms</Link>, <a href={privacyUrl} className="text-primary-600 hover:underline">Privacy Policy</a>, and <a href={mainTermsUrl} className="text-primary-600 hover:underline">Main App Terms</a>. Contact <a href={`mailto:${supportEmail}`} className="text-primary-600 hover:underline">{supportEmail}</a>.
              </p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-6">
              <h3 className="font-semibold text-gray-900 mb-2">No income guarantees</h3>
              <p className="text-sm text-gray-600">
                Any payout examples are illustrative only. Actual commission depends on audience quality, genuine user activity, verified Premium payments, and compliance with the program rules.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Join Better Me Affiliates</h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">Partner with a platform that truly helps people grow</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-lg mb-2">Real Impact</h3>
              <p className="text-gray-600 text-sm">Support journaling, routines, and daily self-reflection without making clinical claims</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-lg mb-2">Fair Payouts</h3>
              <p className="text-gray-600 text-sm">Get paid weekly with R{minimumPayoutAmount.toFixed(2)} minimum payout threshold</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-lg mb-2">Easy Tracking</h3>
              <p className="text-gray-600 text-sm">Real-time dashboard to monitor your earnings and referrals</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto text-center">
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Join Our Growing Community</h2>
            <div className="flex items-center justify-center gap-8">
              <div className="text-center">
                <div className="text-xl font-bold text-primary-600">10,000+</div>
                <div className="text-xs text-gray-500">Active Users</div>
              </div>
              <div className="w-px h-10 bg-gray-200"></div>
              <div className="text-center">
                <div className="text-xl font-bold text-primary-600">R50,000+</div>
                <div className="text-xs text-gray-500">Paid to Affiliates</div>
              </div>
              <div className="w-px h-10 bg-gray-200"></div>
              <div className="text-center">
                <div className="text-xl font-bold text-primary-600">92%</div>
                <div className="text-xs text-gray-500">User Success</div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </SiteChrome>
  );
};

export default AffiliateLanding;
